import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../api/client'
import {
  recordingLink,
  remoteSessionsQuery,
  ticketRemoteSessionsQuery,
  type RemoteSessionRecording,
  type RemoteSessionRow,
  type RemoteSessionsFilter,
} from '../api/remoteConnectSessions'
import { Forbidden } from '../ui/Forbidden'
import { Spinner } from '../ui/Spinner'
import './RemoteSessions.css'

/**
 * Jurnalul sesiunilor remote (CEC 65): cine, pe ce aparat, cand, cat a durat,
 * tichetul legat, cum a intrat (parola de unica folosinta / acceptul omului),
 * stealth, fisierele transferate si inregistrarea - cu redare si descarcare
 * numai pentru cine are dreptul de inregistrari (`canPlay` vine de la server).
 *
 * Acelasi tabel sta pe pagina din Opteemum (`RemoteSessionsPage`, toate
 * sesiunile din firmele omului) si pe fila Remote Connect a tichetului
 * (`ticket` fix: sesiunile pornite de pe tichetul acela, prin capatul tichetului
 * `/easeedesk/tickets/{id}/remote-sessions`, care cere ca omul sa vada tichetul).
 */

export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds < 0) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`
  return `${s}s`
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return ''
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

const ACCESS: Record<RemoteSessionRow['access'], string> = {
  one_time_password: 'One-time password',
  portal_launch: 'Portal launch',
  unknown: 'Unknown',
}

const RECORDING_STATUS: Record<RemoteSessionRecording['status'], string> = {
  expected: 'Recording expected, not uploaded',
  available: 'Recorded',
  failed: 'Recording failed',
  deleted: 'Recording deleted (retention)',
  unknown: 'Recording: unknown state',
}

function RecordingCell({ session, onPlay }: { session: RemoteSessionRow; onPlay(url: string, title: string): void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  if (session.recordings.length === 0) return <span className="text-muted">—</span>

  const open = (recording: RemoteSessionRecording, download: boolean) => {
    setBusy(true)
    setError('')
    recordingLink(session.id, recording.id, download).then(
      (link) => {
        setBusy(false)
        if (download) {
          window.location.assign(link.url)
        } else {
          onPlay(link.url, `${session.device.name} — ${session.startedAt ?? ''}`)
        }
      },
      (e: unknown) => {
        setBusy(false)
        setError(e instanceof Error ? e.message : String(e))
      },
    )
  }

  return (
    <div className="rcs-recordings">
      {session.recordings.map((recording) => (
        <div key={recording.id} className="rcs-recording">
          <span className={`rcs-rec-status rcs-rec-${recording.status}`} title={RECORDING_STATUS[recording.status]}>
            <i className={`fas ${recording.status === 'available' ? 'fa-video' : 'fa-video-slash'}`} aria-hidden="true"></i>{' '}
            {recording.status === 'available' ? formatBytes(recording.bytes) : RECORDING_STATUS[recording.status]}
          </span>
          {recording.playable && (
            <>
              {' '}
              <button type="button" className="btn btn-default btn-xs rcs-play" disabled={busy} onClick={() => open(recording, false)}>
                <i className="fas fa-play" aria-hidden="true"></i> Play recording
              </button>{' '}
              <button type="button" className="btn btn-default btn-xs rcs-download" disabled={busy} onClick={() => open(recording, true)}>
                <i className="fas fa-download" aria-hidden="true"></i>
              </button>
            </>
          )}
        </div>
      ))}
      {error !== '' && <div className="text-danger rcs-error">{error}</div>}
    </div>
  )
}

export function RemoteSessionsTable({ filter, showTicket = true }: { filter: RemoteSessionsFilter; showTicket?: boolean }) {
  const [page, setPage] = useState(1)
  const [player, setPlayer] = useState<{ url: string; title: string } | null>(null)
  const { data, error } = useQuery(
    filter.ticket !== undefined && filter.ticket > 0
      ? ticketRemoteSessionsQuery(filter.ticket, page)
      : remoteSessionsQuery({ ...filter, page }),
  )

  if (error instanceof ApiError && error.status === 403) return <Forbidden />
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const pages = Math.max(1, Math.ceil(data.total / data.pageSize))

  return (
    <div className="rcs">
      {player !== null && (
        <div className="rcs-player">
          <div className="rcs-player-head">
            <strong>{player.title}</strong>
            <button type="button" className="btn btn-default btn-xs" onClick={() => setPlayer(null)}>
              <i className="fas fa-times" aria-hidden="true"></i> Close
            </button>
          </div>
          <video src={player.url} controls autoPlay preload="metadata" className="rcs-video" />
        </div>
      )}

      {data.rows.length === 0 ? (
        <div className="note note-info rcs-empty">
          <i className="fas fa-info-circle" aria-hidden="true"></i> No remote sessions recorded.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-condensed rcs-table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Ended</th>
                <th>Device</th>
                <th>Technician</th>
                <th>Duration</th>
                {showTicket && <th>Ticket</th>}
                <th>Access</th>
                <th>Files</th>
                <th>Recording</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.id} className="rcs-row">
                  <td>
                    {row.startedAt ?? '—'}
                    {row.stealth && (
                      <span className="label label-warning rcs-stealth" title="Connected without notifying the user">
                        stealth
                      </span>
                    )}
                  </td>
                  <td className="rcs-ended">{row.endedAt ?? '—'}</td>
                  <td>
                    <a href={`/app/opteemum/device/${row.device.id}`}>{row.device.name}</a>
                  </td>
                  <td>{row.operator.name || <span className="text-muted">unknown</span>}</td>
                  <td>
                    {row.endedAt === null ? (
                      <span className="label label-success">open</span>
                    ) : (
                      formatDuration(row.durationSeconds)
                    )}
                    {row.stale && (
                      <span className="text-muted" title="Closed by the cleanup job, not by the agent">
                        {' '}
                        (assumed)
                      </span>
                    )}
                  </td>
                  {showTicket && <td>{row.ticketId !== null ? <a href={`/app/easeedesk/ticket/${row.ticketId}`}>#{row.ticketId}</a> : '—'}</td>}
                  <td>{ACCESS[row.access]}</td>
                  <td>{row.transfers > 0 ? row.transfers : '—'}</td>
                  <td>
                    <RecordingCell session={row} onPlay={(url, title) => setPlayer({ url, title })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="rcs-pager">
          <button type="button" className="btn btn-default btn-xs" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            ‹ Newer
          </button>{' '}
          <span className="text-muted">
            page {page} of {pages} ({data.total} sessions)
          </span>{' '}
          <button type="button" className="btn btn-default btn-xs" disabled={page >= pages} onClick={() => setPage(page + 1)}>
            Older ›
          </button>
        </div>
      )}
    </div>
  )
}

/** Opteemum > Remote Connect > Session log: `/app/opteemum/remote_sessions`. */
export function RemoteSessionsPage() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  return (
    <div id="content">
      <div className="rcs-page">
        <div className="rcs-head">
          <h1 className="page-title">
            <i className="fas fa-history" aria-hidden="true"></i> Remote sessions
          </h1>
          <div className="rcs-filter">
            <label>
              From <input type="date" className="form-control input-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>{' '}
            <label>
              To <input type="date" className="form-control input-sm" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
        </div>
        <RemoteSessionsTable filter={{ from, to }} />
      </div>
    </div>
  )
}
